import {
	refreshTokenRequest,
	loginRequest,
	loginSuccess,
	logoutRequest,
	authInitState,
	getTokenSuccess,
	getTokenRequest,
	getConnectRequest,
	getConnectSuccess,
	getPasswordPolicyRequest,
	getPasswordPolicySuccess,
	getAvailableReportTypesRequest,
	getAvailableReportTypesSuccess,
	saveUrl,
	refreshTokenSuccess,
	refreshTokenByTimeOutRequest,
	refreshTokenByTimeOutSuccess,
	setRefreshTokenByTimeOutLoading,
} from 'redux/reducers/auth/reducer';
import axios from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { notificationContainer } from 'utils/notificationContainer';
import { api, responseErrors } from 'services';
import { DateTime } from 'luxon';

import { PATHS } from 'routes/constants';
import {
	IAdminAuthUserResponseData,
	ILoginRequestPayload,
	IAdminLogoutData,
	ITokenRequestPayload,
	ITokenResponsePayload,
	IAuthConnectRequestPayload,
	IAuthConnectResponsePayload,
	ILogoutRequestPayload,
	TGetAvailableReportTypesResponsePayload,
	IAuthRefreshTokenRequestPayload,
	IAuthRefreshTokenResponsePayload,
} from './types';
import { getAuthConnect, getRefreshTime, getRefreshTokenLoader } from './selectors';

// =============================================================:
function* loginRequestWorker(action: PayloadAction<ILoginRequestPayload>) {
	const { payload } = action;

	try {
		const response: IAdminAuthUserResponseData = yield call(api.auth.login, payload);

		yield put(loginSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error?.response?.data?.errors?.totp) {
				const code: string = error?.response?.data?.errors?.totp[0];
				notificationContainer(code, 'info');
			} else {
				notificationContainer('The login or password is incorrect ', 'error');
			}
		}
	}
}

// =============================================================:
function* getTokenWorker(action: PayloadAction<ITokenRequestPayload>) {
	const { payload } = action;

	try {
		const response: ITokenResponsePayload = yield call(api.auth.tokenCode, payload);

		yield put(getTokenSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			if (error?.response?.data?.errors?.totp) {
				const code: string = error?.response?.data?.errors?.totp[0];
				notificationContainer(code, 'info');
			} else {
				notificationContainer('The login or password is incorrect ', 'error');
			}
		}
	}
}

// =============================================================:
function* logoutRequestWorker(action: PayloadAction<ILogoutRequestPayload>) {
	const { payload } = action;

	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.auth.logout, payload);
		yield put(authInitState());
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
		window.location.href = PATHS.HOME_PAGE;
	}
}

// =============================================================:

function* refreshTokenWorker(action: PayloadAction<IAuthRefreshTokenRequestPayload>) {
	const { payload } = action;
	try {
		// console.log(DateTime.now().toFormat('HH:mm:ss'), 'refreshTokenWorker payload', payload);

		const response: IAuthConnectResponsePayload = yield call(api.auth.refreshToken, payload);
		if (response?.access_token && response?.refresh_token) yield put(getConnectSuccess(response));
		yield put(refreshTokenSuccess(Date.now() + 1000 * (response?.expires_at ?? 0) - 10_000));

		// yield put(getTokenSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
		window.location.href = PATHS.HOME_PAGE;
	}
}

// =============================================================:

export function* refreshTokenByTimeOutWorker() {
	try {
		// console.log('refreshTokenByTimeOutWorker started');
		// console.log('----------------------------');
		const refreshTime: number = yield select(getRefreshTime);
		// console.log('refreshTime:', refreshTime, 'current time:', Date.now());
		// console.log(
		// 	DateTime.now().toFormat('HH:mm:ss'),
		// 	'refreshTokenByTimeOutWorker START refreshTime',
		// 	DateTime.fromMillis(refreshTime).toFormat('yyyy.MM.dd HH:mm:ss'),
		// );
		const refreshTokenIsLoading: boolean = yield select(getRefreshTokenLoader);
		// console.log('refreshTokenIsLoading:', refreshTokenIsLoading);
		// console.log(
		// 	DateTime.now().toFormat('HH:mm:ss'),
		// 	'refreshTokenByTimeOutWorker Is Loading',
		// 	refreshTokenIsLoading,
		// );

		if (!refreshTime) {
			// console.log('No refreshTime, calling refreshTokenByTimeOutSuccess');
			yield put(refreshTokenByTimeOutSuccess());
			return;
		}
		if (refreshTime > Date.now()) {
			// console.log('refreshTime > Date.now(), calling refreshTokenByTimeOutSuccess');
			yield put(refreshTokenByTimeOutSuccess());
			// console.log(
			// 	DateTime.now().toFormat('HH:mm:ss'),
			// 	'refreshTokenByTimeOutWorker refreshTime > Date.now',
			// );
			return;
		}
		if (refreshTokenIsLoading) {
			// console.log('refreshTokenIsLoading is true, returning');
			return;
		}

		const payload: IAuthConnectResponsePayload = yield select(getAuthConnect);
		// console.log('Auth connect payload exists:', !!payload?.refresh_token);
		// console.log(DateTime.now().toFormat('HH:mm:ss'), 'REFRESH_TOKEN', !!payload?.refresh_token);
		if (!payload?.refresh_token) {
			// console.log('No refresh token, returning');
			return;
		}
		// console.log(
		// 	DateTime.now().toFormat('HH:mm:ss'),
		// 	'refreshTokenByTimeOutWorker payload',
		// 	payload,
		// );

		// console.log('Calling setRefreshTokenByTimeOutLoading');
		yield put(setRefreshTokenByTimeOutLoading());
		const response: IAuthConnectResponsePayload = yield call(api.auth.refreshToken, payload);
		// console.log('Refresh token response:', !!response?.access_token);

		if (response?.access_token && response?.refresh_token) {
			// console.log('Setting new tokens');
			yield put(getConnectSuccess(response));

			const refreshTimeNew: number = Date.now() + 1000 * (response?.expires_at ?? 0) - 10_000;

			yield put(refreshTokenSuccess(refreshTimeNew));
			// console.log(
			// 	DateTime.now().toFormat('HH:mm:ss'),
			// 	'refreshTokenByTimeOutWorker Success refreshTimeNew',
			// 	DateTime.fromMillis(refreshTimeNew).toFormat('HH:mm:ss'),
			// );
			yield put(refreshTokenByTimeOutSuccess());
		}
	} catch (error) {
		console.error('refreshTokenByTimeOutWorker error:', error);
		if (axios.isAxiosError(error)) {
			console.error('Axios error in refreshToken:', error.response?.data);
			responseErrors(error);
		}
		yield put(authInitState());
		window.location.href = PATHS.HOME_PAGE;
	}
}

// =============================================================:

function* getConnectWorker(action: PayloadAction<IAuthConnectRequestPayload>) {
	const { payload } = action;

	try {
		// yield saveUrl(window.location.pathname + window.location.search);
		// console.log(DateTime.now().toFormat('HH:mm:ss'), 'getConnectWorker payload', payload);

		const response: IAuthConnectResponsePayload = yield call(api.auth.connect, payload);

		yield put(loginSuccess({ token: response?.access_token ?? null }));
		yield put(getConnectSuccess(response));
		yield put(refreshTokenSuccess(Date.now() + 1000 * (response?.expires_at ?? 0) - 10_000));
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
		window.location.href = PATHS.HOME_PAGE;
	}
}

function* getPasswordPolicyWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: string = yield call(api.auth.passwordPolicy);

		yield put(getPasswordPolicySuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) responseErrors(error);
		yield put(authInitState());
	}
}

function* getAvailableReportTypesWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TGetAvailableReportTypesResponsePayload = yield call(
			api.reports.getAvailableReportTypes,
		);
		yield put(getAvailableReportTypesSuccess(response));
	} catch (error) {
		yield put(getAvailableReportTypesSuccess(null));
	}
}

// =============================================================:
export function* authSaga() {
	yield takeEvery(loginRequest.type, loginRequestWorker);
	yield takeEvery(refreshTokenRequest.type, refreshTokenWorker);
	yield takeEvery(logoutRequest.type, logoutRequestWorker);
	yield takeEvery(getTokenRequest.type, getTokenWorker);
	yield takeEvery(getConnectRequest.type, getConnectWorker);
	yield takeEvery(getPasswordPolicyRequest.type, getPasswordPolicyWorker);
	yield takeEvery(getAvailableReportTypesRequest.type, getAvailableReportTypesWorker);
	yield takeEvery(refreshTokenByTimeOutRequest.type, refreshTokenByTimeOutWorker);
}
